import { Card, Table, Tag } from '@humanforce/mango-ui-react';
import type { TableColumn } from '@humanforce/mango-ui-react';
import type { ReactNode } from 'react';
import type { PublicHolidayStatus } from './publicHolidayTypes';

export interface PublicHolidayListRow {
  id: string;
  holidayDate: string;
  name: string;
  scope: string;
  jurisdictionCode: string | null;
  observedDate: string | null;
  source: string | null;
  officialSourceTitle?: string | null;
  status?: PublicHolidayStatus;
  isPartialDay?: boolean;
  isSystemOwned?: boolean;
}

export interface PublicHolidayListProps {
  rows: PublicHolidayListRow[];
  mode: 'admin' | 'tenant';
  title?: string;
  renderActions?: (row: PublicHolidayListRow) => ReactNode;
  columns?: TableColumn[];
  selectable?: boolean;
  selection?: PublicHolidayListRow[];
  onSelectionChange?: (rows: PublicHolidayListRow[]) => void;
}
const formatDate = (value: string | null) => value ? new Date(`${value.slice(0, 10)}T00:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

export const PublicHolidayList = ({ rows, mode, title, renderActions, columns: suppliedColumns, selectable, selection, onSelectionChange }: PublicHolidayListProps) => {
  const defaultColumns: TableColumn[] = [
    { field: 'holidayDate', header: 'Date', sortable: true, body: (row: PublicHolidayListRow) => formatDate(row.holidayDate) },
    { field: 'name', header: 'Holiday', sortable: true },
    { field: 'scope', header: 'Applies to', sortable: true, body: (row: PublicHolidayListRow) => row.jurisdictionCode ?? row.scope },
    { field: 'observedDate', header: 'Observed date', body: (row: PublicHolidayListRow) => formatDate(row.observedDate) },
    { field: 'status', header: 'Status', sortable: true, body: (row: PublicHolidayListRow) => <Tag label={row.status ?? 'Draft'} tone={row.status === 'Published' ? 'positive' : 'grey'} /> },
    ...(renderActions ? [{ field: 'actions', header: 'Actions', body: renderActions }] : []),
  ];
  const columns = suppliedColumns ?? defaultColumns;
  const resolvedColumns = renderActions && !columns.some((column) => column.field === 'actions')
    ? [...columns, { field: 'actions', header: 'Actions', body: renderActions }]
    : columns;
  const table = <Table value={rows} columns={resolvedColumns} emptyMessage="No public holidays" scrollable selectable={selectable} selection={selection} onSelectionChange={(next) => onSelectionChange?.(Array.isArray(next) ? next : [next])} />;
  return title ? <Card title={title}><div className="mgo-stack mgo-gap-m">{table}{mode === 'tenant' ? <span className="mgo-text-s mgo-color-muted">{rows.length} {rows.length === 1 ? 'holiday' : 'holidays'}</span> : null}</div></Card> : table;
};
